import { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, X, FileText, Image } from "lucide-react";

const ACCEPT = "image/jpeg,image/png,image/webp,image/gif,application/pdf";

interface AprovarComComprovanteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  titulo: string;
  descricao?: string;
  onConfirm: (file: File) => Promise<void>;
  isPending?: boolean;
  successMessage?: string;
}

export function AprovarComComprovanteDialog({
  open,
  onOpenChange,
  titulo,
  descricao = "Anexe o comprovante de pagamento (PDF ou imagem). O arquivo ficará visível para futuras confirmações.",
  onConfirm,
  isPending = false,
  successMessage = "Recebimento aprovado com comprovante anexado!",
}: AprovarComComprovanteDialogProps) {
  const [arquivo, setArquivo] = useState<{ file: File; nome: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isPdf = file.type === "application/pdf";
    const isImage = file.type.startsWith("image/");
    if (!isPdf && !isImage) {
      toast.error("Envie um PDF ou imagem (JPEG, PNG, WebP, GIF).");
      e.target.value = "";
      return;
    }
    const maxSize = 20 * 1024 * 1024; // 20MB
    if (file.size > maxSize) {
      toast.error("Arquivo muito grande. Máximo 20MB.");
      e.target.value = "";
      return;
    }
    setArquivo({ file, nome: file.name });
    e.target.value = "";
  };

  const handleConfirm = async () => {
    if (!arquivo) {
      toast.error("Anexe o comprovante de pagamento.");
      return;
    }
    try {
      await onConfirm(arquivo.file);
      toast.success(successMessage);
      setArquivo(null);
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao aprovar.");
    }
  };

  const handleClose = () => {
    setArquivo(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600">{descricao}</p>
        <div className="space-y-3">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={handleFileChange}
          />
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => inputRef.current?.click()}
          >
            <Upload className="h-4 w-4 mr-2" />
            {arquivo ? "Trocar arquivo" : "Selecionar comprovante (PDF ou imagem)"}
          </Button>
          {arquivo && (
            <div className="flex items-center justify-between px-3 py-2 border rounded-md bg-gray-50">
              <span className="flex items-center gap-2 truncate text-sm">
                {arquivo.file.type.startsWith("image/") ? (
                  <Image className="h-4 w-4 text-blue-600 shrink-0" />
                ) : (
                  <FileText className="h-4 w-4 text-red-600 shrink-0" />
                )}
                {arquivo.nome}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="shrink-0 text-red-600 hover:text-red-700"
                onClick={() => setArquivo(null)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!arquivo || isPending}
          >
            {isPending ? "Aprovando..." : "Aprovar e anexar comprovante"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
